import { Injectable } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpHeaders,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Observable, of, throwError, timer } from 'rxjs';
import { delay, mergeMap } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import * as data from './mock-data';

/**
 * DEV-ONLY API mock.
 *
 * When `environment.devMockApi` is on (only environment.mock.ts sets it),
 * backend calls are answered from fixtures in mock-data.ts instead of going
 * over the network, so every screen has data without a real session.
 * Requests to the dev server itself (bundles, assets, HMR) pass through.
 *
 * Registered ahead of TokenInterceptor in app.config.ts so a matched request
 * short-circuits before any auth header or 401-refresh logic runs.
 *
 * Responses copy the real envelopes: `{ items, itemPagination }` where the
 * API wraps lists, bare arrays plus `X-Total-Count` where Spring pages them,
 * plain numbers for counts. Lists honour JHipster criteria (`field.contains`,
 * `.equals`, `.in`, ranges), `sort=field,dir` and `page`/`size`.
 *
 * Writes return what the API returns. Message archive and cancel, campaign
 * edits, test contacts, team members, USSD applications and the logo and
 * profile image change the fixtures for the rest of the session; other writes
 * are acknowledged without changing lists.
 *
 * Error states you can trigger on purpose:
 * - /new-password?token=expired (or =used) shows the invalid-link state;
 *   resetting with token=locked answers 423, token=bad answers 400.
 * - Forgot password with an email containing "throttle" answers 429.
 * - Changing your password with current password "wrong" answers 400.
 * - Inviting a teammate with an email already on the team answers 409.
 * - Creating a contact with a phone number already on file answers 400.
 * - USSD simulator: an unknown or disabled service code ends the session
 *   with an error.
 */

/** Hosts whose traffic this interceptor owns. */
const API_HOST_PATTERN = /(blackkonnect\.io|ondigitalocean\.app|amazonaws\.com)/i;
/** USSD services are reached through same-origin dev-proxy paths. */
const LOCAL_API_PATH = /^\/ussd-(?:apps-|sim-)?api\//;

const LATENCY_MS = 140;

const STATUS_TEXT: Record<number, string> = {
  400: 'Bad Request',
  404: 'Not Found',
  409: 'Conflict',
  423: 'Locked',
  429: 'Too Many Requests',
};

/** A handler result that needs a status or headers. */
class MockReply {
  constructor(
    readonly body: unknown,
    readonly status = 200,
    readonly headers: Record<string, string> = {}
  ) {}
}

const reply = (body: unknown, status = 200, headers: Record<string, string> = {}) =>
  new MockReply(body, status, headers);

const fail = (status: number, message: string, extra: Record<string, unknown> = {}) =>
  new MockReply({ status, title: STATUS_TEXT[status] ?? 'Error', message, detail: message, ...extra }, status);

const notFound = (what: string) => fail(404, `No ${what} matches that id.`);

/** Bare array with the total in X-Total-Count, as Spring list endpoints answer. */
const springList = (rows: unknown[], total: number) =>
  reply(rows, 200, { 'X-Total-Count': String(total) });

const noContent = () => reply(null, 204);

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

interface Ctx {
  path: string;
  query: URLSearchParams;
  match: RegExpMatchArray;
  method: string;
  body: any;
}

type Handler = (ctx: Ctx) => unknown;

interface Rule {
  methods: readonly string[];
  pattern: RegExp;
  handler: Handler;
}

const on = (methods: Method | Method[], pattern: RegExp, handler: Handler): Rule => ({
  methods: Array.isArray(methods) ? methods : [methods],
  pattern,
  handler,
});

/** Service prefixes, with the rest of a path matched to the end. */
const E = '/blackkonnectcxpengagement/api';
const C = '/blackkonnectcxpcontacts/api';
const at = (prefix: string, rest: string) => new RegExp(`${prefix}${rest}$`);

const ID = '([^/]+)';
const ORG = '[^/]+';

/** Find or 404. */
function found<T>(value: T | undefined, what: string, then: (value: T) => unknown = (v) => v): unknown {
  return value === undefined ? notFound(what) : then(value);
}

const rules: Rule[] = [
  // ------------------------------------------------------------- dashboard
  on('GET', at(E, '/dashboard/organization/user/profile/[^/]+'), () => data.MOCK_USER_PROFILE),
  on('GET', at(E, `/dashboard/${ORG}/stats`), () => data.dashboardStats()),
  on('GET', at(E, `/dashboard/${ORG}/summary/campaigns`), ({ query }) =>
    data.MOCK_CAMPAIGNS.slice(0, Number(query.get('size') ?? 6))
  ),
  on('GET', at(E, `/dashboard/${ORG}/summary/messages`), ({ query }) =>
    data.MOCK_MESSAGES.slice(0, Number(query.get('size') ?? 6))
  ),
  // SMSC gateway log: List<SmsReportData>, no envelope and no total.
  on('GET', at(E, `/dashboard/messages/${ORG}/sms/reports/page`), ({ query }) => data.gatewayPage(query)),
  on('GET', at(E, `/dashboard/messages/${ORG}/sms/reports/summary`), ({ query }) => data.gatewaySummary(query)),
  on('GET', at(E, `/dashboard/messages/${ORG}/sent/overview`), ({ query }) => data.sentOverview(query)),
  on('GET', at(E, `/dashboard/messages/${ORG}/sms/reports`), ({ query }) => data.gatewayLog(query)),

  // -------------------------------------------------------------- messages
  // Ordered before the `{uuid}` rules, which would otherwise swallow "count".
  on('GET', at(E, '/engagement-messages/count'), ({ query }) => data.messageQuery(query).length),
  on('GET', at(E, `/organization/${ORG}/engagement-messages/count`), ({ query }) => data.messageQuery(query).length),
  on('GET', at(E, `/v1/organization/${ORG}/engagement-messages`), ({ query }) =>
    data.paginate(data.messageQuery(query), query)
  ),
  on('POST', at(E, `/v1/organization/${ORG}/engagement-messages`), ({ body }) => reply(data.createMessage(body), 201)),
  on('GET', at(E, `/v1/organization/${ORG}/engagement-messages/${ID}/receipients`), ({ match }) =>
    found(data.messageRecipients(match[1]), 'message')
  ),
  on('POST', at(E, `/organization/${ORG}/engagement-messages/preview/recipient-count`), ({ body }) => ({
    totalRecipients: data.previewRecipientCount(body),
  })),
  on('POST', at(E, `/organization/${ORG}/engagement-messages/preview`), ({ body }) => data.previewRecipients(body)),
  on('GET', at(E, `/organization/${ORG}/engagement-messages/${ID}`), ({ match }) =>
    found(data.findAnyMessage(match[1]), 'message')
  ),
  on('PATCH', at(E, `/organization/${ORG}/engagement-messages/${ID}`), ({ match, body }) =>
    found(data.updateMessage(match[1], body), 'message')
  ),
  on('DELETE', at(E, `/organization/${ORG}/engagement-messages/${ID}`), ({ match }) =>
    data.deleteMessage(match[1]) ? noContent() : notFound('message')
  ),
  // MessageService.updateMessage / deleteMessage call routes the backend does
  // not have. Acknowledge so flows continue, but say so in the console.
  on(['PATCH', 'DELETE'], at(E, `/${ORG}/engagement-messages/${ID}`), ({ method, path, body }) => {
    console.warn(`[mock-api] ${method} ${path} has no backend route; the real API answers 404.`);
    return method === 'DELETE' ? noContent() : body ?? { success: true };
  }),
  on('GET', at(E, `/engagement-messages/${ID}`), ({ match }) => found(data.findAnyMessage(match[1]), 'message')),
  on('GET', at(E, `/messages/${ID}`), ({ match }) => found(data.findAnyMessage(match[1]), 'message')),

  // ------------------------------------------------------ delivery receipts
  on('GET', at(E, `/v1/engagement-message-reports/organization/${ORG}/message/${ID}/charts`), ({ match, query }) =>
    data.messageCharts(match[1], query)
  ),
  on('GET', at(E, `/v1/engagement-message-reports/organization/${ORG}/message/${ID}`), ({ match, query }) => {
    const { rows, total } = data.messageReport(match[1], query);
    return springList(rows, total);
  }),
  on('GET', at(E, `/v1/engagement-message-reports/organization/${ORG}`), ({ query }) => data.organisationReports(query)),
  on('GET', at(E, '/engagement-message-reports/count'), ({ query }) => data.reportCount(query)),
  on('GET', at(E, '/engagement-message-reports'), ({ query }) => {
    const { rows, total } = data.reportList(query);
    return springList(rows, total);
  }),

  // --------------------------------------------------- sending and senders
  // The backend answers 202 with no body and processes rows in the background.
  on('POST', at(E, `/v1/organization/${ORG}/smart-broadcasts`), () => reply(null, 202)),
  on('POST', at(E, `/v1/organization/${ORG}/recipient-batches`), ({ body }) => reply(data.recipientBatch(body), 201)),
  on('GET', at(E, `/v1/sender-ids/organisations/${ORG}`), () => data.MOCK_SENDER_IDS),

  // ------------------------------------------------------------- templates
  on('GET', at(E, `/message-templates/organizations/${ORG}`), ({ query }) => data.templatePage(query)),
  on('POST', at(E, `/message-templates/${ORG}`), ({ body }) => reply(data.newTemplate(body), 201)),
  on('GET', at(E, `/v1/engagement-templates/organisations/${ORG}/count`), ({ query }) =>
    data.query(data.MOCK_TEMPLATES, query).length
  ),
  on('GET', at(E, `/v1/engagement-templates/organisations/${ORG}/${ID}`), ({ match }) =>
    found(data.findTemplate(match[1]), 'template')
  ),
  on('PUT', at(E, `/v1/engagement-templates/organisations/${ORG}/${ID}`), ({ match, body }) =>
    found(data.findTemplate(match[1]), 'template', (template) => ({ ...template, ...body, updatedAt: new Date().toISOString() }))
  ),
  on('GET', at(E, `/v1/engagement-templates/organisations/${ORG}`), ({ query }) =>
    data.paginate(data.query(data.MOCK_TEMPLATES, query, { sort: 'createdAt,desc' }), query)
  ),
  on('GET', at(E, '/engagement-message-field-definitions/count'), ({ query }) =>
    data.query(data.MOCK_FIELD_DEFINITIONS, query).length
  ),
  on('GET', at(E, '/engagement-message-field-definitions'), ({ query }) => {
    const all = data.query(data.MOCK_FIELD_DEFINITIONS, query);
    return springList(data.pageSlice(all, query), all.length);
  }),
  on('GET', at(E, '/engagement-contact-restriction-logs/count'), ({ query }) =>
    data.query(data.MOCK_RESTRICTION_LOGS, query).length
  ),
  on('GET', at(E, '/engagement-contact-restriction-logs'), ({ query }) => {
    const all = data.query(data.MOCK_RESTRICTION_LOGS, query, { sort: 'createdAt,desc' });
    return springList(data.pageSlice(all, query), all.length);
  }),

  // --------------------------------------------------------- test contacts
  on('GET', at(E, `/organization/${ORG}/message-test-contacts/count`), ({ query }) =>
    data.query(data.MOCK_TEST_CONTACTS, query).length
  ),
  on('POST', at(E, `/organization/${ORG}/message-test-contacts/send-test-message`), ({ body }) =>
    found(data.sendTestMessage(body), 'test contact')
  ),
  on('GET', at(E, `/organization/${ORG}/message-test-contacts`), ({ query }) => {
    const all = data.query(data.MOCK_TEST_CONTACTS, query, { sort: 'createdAt,desc' });
    return springList(data.pageSlice(all, query), all.length);
  }),
  on('POST', at(E, `/organization/${ORG}/message-test-contacts`), ({ body }) => reply(data.createTestContact(body), 201)),
  on('PATCH', at(E, `/message-test-contacts/${ID}`), ({ match, body }) =>
    found(data.findTestContact(match[1]), 'test contact', (contact) => Object.assign(contact, body ?? {}, { id: contact.id }))
  ),
  on('DELETE', at(E, `/message-test-contacts/${ID}`), ({ match }) => {
    const index = data.MOCK_TEST_CONTACTS.findIndex((contact) => String(contact.id) === match[1] || contact.uuid === match[1]);
    if (index < 0) return notFound('test contact');
    data.MOCK_TEST_CONTACTS.splice(index, 1);
    return noContent();
  }),

  // ------------------------------------------------------------- campaigns
  on('GET', at(E, `/engagement-campaigns/v2/organisations/${ORG}`), ({ query }) =>
    data.paginate(data.query(data.MOCK_CAMPAIGNS, query, { sort: 'createdAt,desc' }), query)
  ),
  on('POST', at(E, `/engagement-campaigns/v1/organisations/${ORG}`), ({ body }) => reply(data.createCampaign(body), 201)),
  on('GET', at(E, `/engagement-campaigns/v1/organisations/${ORG}/campaign/${ID}/receipients`), ({ match }) =>
    found(data.findCampaign(match[1]), 'campaign', data.campaignRecipients)
  ),
  on('GET', at(E, `/engagement-campaigns/v1/organisations/${ORG}/campaign/${ID}/details/stat`), ({ match }) =>
    found(data.findCampaign(match[1]), 'campaign', data.campaignStats)
  ),
  on('GET', at(E, `/engagement-campaigns/v1/organisations/${ORG}/campaign/${ID}`), ({ match }) =>
    found(data.findCampaign(match[1]), 'campaign')
  ),
  on('POST', at(E, `/engagement-campaigns/organisations/${ORG}/preview/recipient-count`), ({ body }) => ({
    totalRecipients: data.previewRecipientCount(body),
  })),
  on('GET', at(E, `/engagement-campaigns/organisations/${ORG}/${ID}`), ({ match }) =>
    found(data.findCampaign(match[1]), 'campaign')
  ),
  on(['PUT', 'PATCH'], at(E, `/engagement-campaigns/organisations/${ORG}/${ID}`), ({ match, body }) =>
    found(data.updateCampaign(match[1], body), 'campaign')
  ),
  on('DELETE', at(E, `/engagement-campaigns/organisations/${ORG}/${ID}`), ({ match }) => {
    const index = data.MOCK_CAMPAIGNS.findIndex((campaign) => String(campaign.id) === match[1] || campaign.uuid === match[1]);
    if (index < 0) return notFound('campaign');
    data.MOCK_CAMPAIGNS.splice(index, 1);
    return noContent();
  }),
  // Legacy list: Page content, no envelope.
  on('GET', at(E, `/engagement-campaigns/organisations/${ORG}`), () => data.MOCK_CAMPAIGNS),

  on('GET', at(E, `/v1/engagement-campaign-message-reports/organization/${ORG}/campaign/${ID}/messages/charts`), ({ match }) =>
    found(data.findCampaign(match[1]), 'campaign', data.campaignMessagesCharts)
  ),
  on('GET', at(E, `/v1/engagement-campaign-message-reports/organization/${ORG}/campaign/${ID}`), ({ match }) =>
    found(data.findCampaign(match[1]), 'campaign', data.campaignMessagesReport)
  ),
  on('GET', at(E, `/v1/engagement-campaign-recipient-reports/organization/${ORG}/campaign/${ID}/recipient/charts`), ({ match }) =>
    found(data.findCampaign(match[1]), 'campaign', data.campaignRecipientCharts)
  ),
  on('GET', at(E, `/v1/engagement-campaign-recipient-reports/organization/${ORG}/campaign/${ID}`), ({ match }) =>
    found(data.findCampaign(match[1]), 'campaign', data.campaignRecipientsReport)
  ),

  // -------------------------------------------------------------- contacts
  // Counts first: they share prefixes with the list and detail rules.
  on('GET', at(C, `/person-entities/organisations/${ORG}/count`), () => data.MOCK_CONTACTS.length),
  on('GET', at(C, `/person-entities/organisations/${ORG}/contacts/companies/${ID}/count`), ({ match }) =>
    data.groupMembers('companies', match[1]).length
  ),
  on('GET', at(C, `/person-entities/organisations/${ORG}/contacts/tags/${ID}/count`), ({ match }) =>
    data.groupMembers('tags', match[1]).length
  ),
  on('GET', at(C, `/person-entities/organisations/${ORG}/entity-segments/${ID}/contacts/count`), ({ match }) =>
    data.groupMembers('segments', match[1]).length
  ),
  on('GET', at(C, `/person-entities/organisations/${ORG}/contacts/data/download/csv`), () =>
    reply(data.contactsCsv(), 200, { 'Content-Type': 'text/csv', 'Content-Disposition': 'attachment; filename="contacts.csv"' })
  ),
  on('GET', at(C, `/organisations/${ORG}/contacts/data/download/csv/template`), () =>
    reply(data.contactsCsv(true), 200, { 'Content-Type': 'text/csv', 'Content-Disposition': 'attachment; filename="contacts-template.csv"' })
  ),
  on('POST', at(C, `/person-entities/organisations/${ORG}/contacts/batch/import/validation`), ({ body }) =>
    data.validateContactsBatch(body)
  ),
  on('POST', at(C, `/person-entities/organisations/${ORG}/contacts/batch/import`), ({ body }) =>
    data.importContactsBatch(body)
  ),
  // Returns the contacts actually saved; numbers already on file are skipped.
  on('POST', at(C, `/person-entities/organisations/${ORG}/contacts/batch`), ({ body }) =>
    reply(
      (Array.isArray(body?.personEntityList) ? body.personEntityList : [])
        .filter((contact: any) => !data.isTakenNumber(contact?.primaryContactNumber))
        .map((contact: any, index: number) => data.newContact(contact, index)),
      201
    )
  ),
  on('GET', at(C, `/(?:v2/)?person-entities/organisations/${ORG}/contacts/(companies|tags|segments)/${ID}`), ({ match, query }) =>
    data.paginate(
      data.query(data.groupMembers(match[1] as data.GroupKind, match[2]), query, { get: data.contactGet }),
      query
    )
  ),
  on('GET', at(C, `/(?:v2/)?person-entities/organisations/${ORG}/contacts/${ID}`), ({ match }) =>
    found(data.findContact(match[1]), 'contact')
  ),
  on('GET', at(C, `/(?:v2/)?person-entities/organisations/${ORG}/contacts`), ({ query }) =>
    data.paginate(data.query(data.MOCK_CONTACTS, query, { get: data.contactGet }), query)
  ),
  on('POST', at(C, `/person-entities/organisations/${ORG}/contacts`), ({ body }) =>
    data.isTakenNumber(body?.primaryContactNumber)
      ? fail(400, 'A contact with this phone number already exists.', { errorKey: 'duplicatePrimaryContactNumber' })
      : reply(data.newContact(body), 201)
  ),

  on('GET', at(C, `/entity-tags/organisations/${ORG}/count`), () => data.MOCK_TAGS.length),
  // Not a bare number: tag-details reads `count.count`.
  on('GET', at(C, `/entity-tags/organisations/${ORG}/tags/${ID}/count`), ({ match }) => ({
    organisationUuid: data.MOCK_ORG_ID,
    tagId: match[1],
    count: data.groupMembers('tags', match[1]).length,
  })),
  on('GET', at(C, `/entity-tags/organisations/${ORG}/entity-tags/${ID}`), ({ match }) => found(data.findTag(match[1]), 'tag')),
  on('POST', at(C, `/entity-tags/organisations/${ORG}`), ({ body }) =>
    reply({ ...data.MOCK_TAGS[0], ...body, id: 100, uuid: 'tag-new-0100', contactsCount: 0, createdAt: new Date().toISOString() }, 201)
  ),
  on('GET', at(C, `/v2/entity-tags/organisations/${ORG}`), ({ query }) => data.paginate(data.query(data.MOCK_TAGS, query), query)),

  on('GET', at(C, `/company-entities/organisations/${ORG}/count`), () => data.MOCK_COMPANIES.length),
  on('GET', at(C, `/company-entities/organisations/${ORG}/companies/${ID}`), ({ match }) =>
    found(data.findCompany(match[1]), 'organisation')
  ),
  on('GET', at(C, `/company-entities/organisations/${ORG}/companies`), () => data.MOCK_COMPANIES),
  on('POST', at(C, `/company-entities/organisations/${ORG}/companies`), ({ body }) =>
    reply({ ...data.MOCK_COMPANIES[0], ...body, id: 100, uuid: 'company-new-0100', contactsCount: 0, createdAt: new Date().toISOString() }, 201)
  ),
  on('GET', at(C, `/v2/company-entities/organisations/${ORG}/companies`), ({ query }) =>
    data.paginate(data.query(data.MOCK_COMPANIES, query), query)
  ),

  on('GET', at(C, `/entity-segments/organisations/${ORG}/count`), () => data.MOCK_SEGMENTS.length),
  on('GET', at(C, `/entity-segments/organisations/${ORG}/entity-segments/${ID}`), ({ match }) =>
    found(data.findSegment(match[1]), 'segment')
  ),
  on('POST', at(C, `/entity-segments/organisations/${ORG}`), ({ body }) =>
    reply({ ...data.MOCK_SEGMENTS[0], ...body, id: 100, uuid: 'segment-new-0100', contactsCount: 0, createdAt: new Date().toISOString() }, 201)
  ),
  on('GET', at(C, `/entity-segments/organisations/${ORG}`), ({ query }) => data.paginate(data.query(data.MOCK_SEGMENTS, query), query)),

  on('GET', /\/api\/contacts-organisation-profiles$/, () => []),

  // ------------------------------------------------------------------- IAM
  on('GET', /\/api\/iam\/customers\/[^/]+$/, () => data.MOCK_ORGANIZATION),
  on('PATCH', /\/api\/iam\/customers\/[^/]+$/, ({ body }) => {
    const { name, description, website, countryCode, registrationNumber, ...attributes } = body ?? {};
    Object.assign(data.MOCK_ORGANIZATION, {
      ...(name ? { name } : {}),
      ...(description !== undefined ? { description } : {}),
      ...(website !== undefined ? { website } : {}),
      ...(countryCode ? { countryCode } : {}),
      ...(registrationNumber !== undefined ? { registrationNumber } : {}),
    });
    const fieldMap: Record<string, string> = { emailAddress: 'email', primaryContactNumber: 'primaryContactNumber', industryType: 'industryType', city: 'city', address: 'address' };
    for (const [key, value] of Object.entries(attributes as Record<string, unknown>)) {
      const target = fieldMap[key];
      if (target && value != null) data.MOCK_ORGANIZATION.attributes[target] = String(value);
    }
    return data.MOCK_ORGANIZATION;
  }),
  on('GET', /\/api\/iam\/organisations\/members\/user-profiles\/organisations\/[^/]+$/, ({ query }) => data.membersPage(query)),
  on('POST', /\/api\/iam\/organisations\/members$/, ({ body }) => {
    const email = String(body?.emailAddress ?? body?.email ?? '').toLowerCase();
    if (email && data.MOCK_MEMBERS.some((member) => member.email.toLowerCase() === email)) {
      return fail(409, 'Someone on your team already uses this email address.');
    }
    return reply(data.createMember(body), 201);
  }),
  on('GET', /\/api\/iam\/organisations\/members\/user-profiles\/user-subjects\/([^/]+)$/, ({ match }) =>
    found(data.findMember(match[1]), 'user')
  ),
  on('PUT', /\/api\/iam\/organisations\/members\/user-profiles\/user-subjects\/([^/]+)$/, ({ match, body }) =>
    found(data.findMember(match[1]), 'user', (member) => {
      const { firstName, lastName, phoneNumber, authorities } = body ?? {};
      if (firstName) member.firstName = String(firstName);
      if (lastName) member.lastName = String(lastName);
      if (phoneNumber) member.attributes['phoneNumber'] = [String(phoneNumber)];
      if (Array.isArray(authorities)) member.realmRoles = authorities.map(String);
      return member;
    })
  ),
  on('PUT', /\/api\/iam\/users\/[^/]+\/password$/, ({ body }) =>
    body?.currentPassword === 'wrong'
      ? fail(400, 'Your current password is incorrect.')
      : { success: true, message: 'Password updated.' }
  ),
  on('PUT', /\/api\/iam\/users\/([^/]+)\/(disable|enable)$/, ({ match }) =>
    found(data.findMember(match[1]), 'user', (member) => {
      member.enabled = match[2] === 'enable';
      return member;
    })
  ),
  on('GET', /\/api\/iam\/users\/[^/]+\/profile-image$/, () =>
    data.IMAGE_STATE.profileImage ? reply(data.PROFILE_IMAGE_SVG, 200, { 'Content-Type': 'image/svg+xml' }) : notFound('profile image')
  ),
  on(['POST', 'PUT'], /\/api\/iam\/users\/[^/]+\/profile-image$/, () => {
    data.IMAGE_STATE.profileImage = true;
    return { success: true };
  }),
  on('DELETE', /\/api\/iam\/users\/[^/]+\/profile-image$/, () => {
    data.IMAGE_STATE.profileImage = false;
    return noContent();
  }),
  on('GET', /\/api\/iam\/organisations\/[^/]+\/logo$/, () =>
    data.IMAGE_STATE.logo ? reply(data.ORGANIZATION_LOGO_SVG, 200, { 'Content-Type': 'image/svg+xml' }) : notFound('logo')
  ),
  on(['POST', 'PUT'], /\/api\/iam\/organisations\/[^/]+\/logo$/, () => {
    data.IMAGE_STATE.logo = true;
    return { success: true };
  }),
  on('DELETE', /\/api\/iam\/organisations\/[^/]+\/logo$/, () => {
    data.IMAGE_STATE.logo = false;
    return noContent();
  }),
  on('GET', /\/api\/iam\/roles\/cxp$/, () => data.MOCK_ROLES),

  // ------------------------------------------------------------------ auth
  on('POST', /\/api\/auth\/forgot-password$/, ({ body }) =>
    /throttle/i.test(String(body?.email ?? ''))
      ? fail(429, 'Too many reset requests. Try again in 15 minutes.')
      : { success: true, message: 'If an account uses that email, a reset link is on its way.' }
  ),
  on('GET', /\/api\/auth\/verify-reset-token$/, ({ query }) => {
    const token = query.get('token') ?? '';
    if (!token) return fail(400, 'The reset link is missing its token.');
    if (token === 'expired') return { valid: false, message: 'This reset link has expired. Request a new one.' };
    if (token === 'used') return { valid: false, message: 'This reset link has already been used. Request a new one.' };
    return { valid: true, email: 'd•••r@blackkonnect.local' };
  }),
  on('POST', /\/api\/auth\/reset-password$/, ({ body }) => {
    if (body?.token === 'locked') return fail(423, 'This account is locked. Ask an administrator to unlock it.');
    if (body?.token === 'bad' || body?.token === 'expired') return fail(400, 'This reset link has expired. Request a new one.');
    return { success: true, message: 'Your password has been reset. Sign in with the new password.' };
  }),

  // ------------------------------------------------------------------ USSD
  // Specific paths before `/applications/{id}`.
  on('GET', /\/organisations\/[^/]+\/ussd\/applications\/stats$/, () => data.ussdStats()),
  on('GET', /\/organisations\/[^/]+\/ussd\/applications\/check-duplicate$/, ({ query }) => {
    const exclude = query.get('excludeId');
    const others = data.MOCK_USSD_APPS.filter((app) => app.id !== exclude);
    const serviceCodeExists = !!query.get('serviceCode') && others.some((app) => app.serviceCode === query.get('serviceCode'));
    const nameExists = !!query.get('name') && others.some((app) => app.name.toLowerCase() === String(query.get('name')).toLowerCase());
    return { exists: serviceCodeExists || nameExists, serviceCodeExists, nameExists };
  }),
  on('GET', /\/organisations\/[^/]+\/ussd\/applications\/(enabled|disabled)$/, ({ match }) =>
    data.MOCK_USSD_APPS.filter((app) => app.enabled === (match[1] === 'enabled'))
  ),
  on('GET', /\/organisations\/[^/]+\/ussd\/applications\/service-code$/, ({ query }) =>
    found(data.MOCK_USSD_APPS.find((app) => app.serviceCode === query.get('serviceCode')), 'application')
  ),
  on('GET', /\/organisations\/[^/]+\/ussd\/applications\/name\/([^/]+)$/, ({ match }) =>
    found(data.MOCK_USSD_APPS.find((app) => app.name.toLowerCase() === decodeURIComponent(match[1]).toLowerCase()), 'application')
  ),
  on('GET', /\/organisations\/[^/]+\/ussd\/applications\/([^/]+)\/promotions$/, ({ match }) =>
    found(data.findUssdApp(match[1]), 'application', data.ussdPromotions)
  ),
  on('POST', /\/organisations\/[^/]+\/ussd\/applications\/([^/]+)\/promote$/, ({ match, body }) =>
    found(data.findUssdApp(match[1]), 'application', (app) => reply(data.promoteUssdApp(app, body), 201))
  ),
  on('POST', /\/organisations\/[^/]+\/ussd\/applications\/([^/]+)\/(enable|disable)$/, ({ match }) =>
    found(data.findUssdApp(match[1]), 'application', (app) => {
      app.enabled = match[2] === 'enable';
      app.modifiedAt = new Date().toISOString();
      return app;
    })
  ),
  on('GET', /\/organisations\/[^/]+\/ussd\/applications\/([^/]+)$/, ({ match }) => found(data.findUssdApp(match[1]), 'application')),
  on('PATCH', /\/organisations\/[^/]+\/ussd\/applications\/([^/]+)$/, ({ match, body }) =>
    found(data.findUssdApp(match[1]), 'application', (app) =>
      Object.assign(app, body ?? {}, { id: app.id, uuid: app.uuid, modifiedAt: new Date().toISOString() })
    )
  ),
  on('DELETE', /\/organisations\/[^/]+\/ussd\/applications\/([^/]+)$/, ({ match }) => {
    const index = data.MOCK_USSD_APPS.findIndex((app) => app.id === match[1] || app.uuid === match[1]);
    if (index < 0) return notFound('application');
    data.MOCK_USSD_APPS.splice(index, 1);
    return noContent();
  }),
  // A bare array with the total in X-Total-Count.
  on('GET', /\/organisations\/[^/]+\/ussd\/applications$/, ({ query }) => {
    const all = data.query(data.MOCK_USSD_APPS, query, { sort: 'createdAt,desc' });
    return springList(data.pageSlice(all, query), all.length);
  }),
  on('POST', /\/organisations\/[^/]+\/ussd\/applications$/, ({ body }) => reply(data.createUssdApp(body), 201)),
  on('POST', /\/ussd\/application\/generate-simulation-code$/, ({ query }) =>
    data.generateServiceCode(query.get('countryCode') ?? 'BW')
  ),
  on('GET', /\/core\/ussd-usage-records\/by-organisation\/[^/]+$/, ({ query }) => data.ussdUsage(query)),
  on('GET', /\/core\/ussd-usage-records\/by-session\/([^/]+)\/detail$/, ({ match }) =>
    found(data.ussdSessionDetail(match[1]), 'session')
  ),
  on('POST', /\/ussd\/simulator\/request$/, ({ body }) => data.ussdSimulate(body)),
  on('GET', /\/simulator\/telcos\/([^/]+)$/, ({ match }) => {
    const telcos = data.USSD_TELCOS[match[1].toUpperCase()];
    return telcos ? telcos : notFound('operator list for that country');
  }),
  on('GET', /\/simulator\/telcos$/, () => Object.values(data.USSD_TELCOS).flat()),
  on('GET', /\/simulator\/countries$/, () => data.USSD_COUNTRIES),
  on('POST', /\/simulator\/[A-Za-z]{2}\/[^/]+$/, ({ body }) => data.ussdSimulate(body)),
];

/** Copy so components that mutate a response never change the fixtures. */
function clone<T>(value: T): T {
  if (value == null || typeof value !== 'object') return value;
  if (typeof Blob !== 'undefined' && value instanceof Blob) return value;
  try {
    return structuredClone(value);
  } catch {
    return value;
  }
}

@Injectable()
export class MockApiInterceptor implements HttpInterceptor {
  /** Unmatched paths already reported, so the console warns once each. */
  private readonly reported = new Set<string>();

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Only environment.mock.ts defines the flag; every other build reads undefined.
    const { devMockApi } = environment as { devMockApi?: boolean };
    if (!devMockApi) return next.handle(req);

    // urlWithParams, not url: Angular keeps HttpParams out of `url`, and the
    // filters and paging this mock switches on live in the query string.
    const url = new URL(req.urlWithParams, 'http://localhost');
    if (!API_HOST_PATTERN.test(req.url) && !LOCAL_API_PATH.test(url.pathname)) {
      return next.handle(req);
    }
    const path = url.pathname.replace(/\/{2,}/g, '/');

    for (const rule of rules) {
      if (!rule.methods.includes(req.method)) continue;
      const match = path.match(rule.pattern);
      if (!match) continue;
      return this.respond(
        req,
        rule.handler({ path, query: url.searchParams, match, method: req.method, body: req.body })
      );
    }

    // Writes without a rule: acknowledge so the UI shows success.
    if (req.method !== 'GET') {
      const body = req.body instanceof FormData || req.body == null ? { success: true } : req.body;
      return this.respond(req, body);
    }

    if (!this.reported.has(path)) {
      this.reported.add(path);
      console.warn(`[mock-api] no fixture for GET ${path}; returning an empty list.`);
    }
    return this.respond(req, { items: [], itemPagination: data.emptyPagination() });
  }

  /** Small delay so loading spinners and skeletons still get exercised. */
  private respond(req: HttpRequest<any>, result: unknown): Observable<HttpEvent<any>> {
    const outcome = result instanceof MockReply ? result : new MockReply(result);
    const headers = new HttpHeaders(outcome.headers);

    if (outcome.status >= 400) {
      const error = new HttpErrorResponse({
        status: outcome.status,
        statusText: STATUS_TEXT[outcome.status] ?? 'Error',
        error: outcome.body,
        headers,
        url: req.urlWithParams,
      });
      return timer(LATENCY_MS).pipe(mergeMap(() => throwError(() => error)));
    }

    let body: unknown = clone(outcome.body);
    if (req.responseType === 'blob' && typeof body === 'string' && typeof Blob !== 'undefined') {
      body = new Blob([body], { type: outcome.headers['Content-Type'] ?? 'text/plain' });
    }

    return of(
      new HttpResponse({ status: outcome.status, body, headers, url: req.urlWithParams })
    ).pipe(delay(LATENCY_MS));
  }
}
